import { useMemo, useEffect, useState } from "react";
import styles from "./borrowingCapacityResult.module.css";
import CalculatePV from "../../../helpers/calculatePV";
import HemDependant from "../../../helpers/hemDependant";
import HemSingleJoint from "../../../helpers/hemSingleJoint";
import CalculateNetIncome from "../../../helpers/calculateNetIncome";
import BorrowingCapacityResultPay from "./borrowingCapacityResultPay";

const BorrowingCapacityResult = ({
  numberOfApplicantsIn,
  numberOfDependantIn,
  annualIncome1In,
  annualIncome2In,
  monthlyRentalIncomeIn,
  creditCardLimitIn,
  personalLoanRepayIn,
  homeLoanRepayIn,
  yearIn,
  rateIn,            // user-entered rate, e.g. 4.79
  serviceBufferIn,   // e.g. 3 (%)
  showToken = null,
}) => {
  // --- Normalize inputs
  const numberOfApplicants = Number(numberOfApplicantsIn);
  const numberOfDependant = Number(numberOfDependantIn);
  const annualIncome1 = Number(annualIncome1In);
  const annualIncome2 = numberOfApplicants === 2 ? Number(annualIncome2In) : 0;
  const monthlyRentalIncome = Number(monthlyRentalIncomeIn);
  const creditCardLimit = Number(creditCardLimitIn);
  const personalLoanRepay = Number(personalLoanRepayIn);
  const homeLoanRepay = Number(homeLoanRepayIn);
  const year = Number(yearIn);
  const serviceBuffer = Number(serviceBufferIn);
  const enteredRate = Number(rateIn) || 0;

  // --- Helpers
  const monthlyPay = () => {
    if (numberOfApplicants === 0) return 0;

    const hemApplicants = HemSingleJoint(annualIncome1 + annualIncome2, numberOfApplicants);
    const hemDependantAmt = HemDependant(numberOfApplicants, numberOfDependant);

    const expenses =
      creditCardLimit * 0.038 +
      homeLoanRepay +
      personalLoanRepay +
      hemDependantAmt +
      hemApplicants;

    if (numberOfApplicants === 1) {
      const netIncome = CalculateNetIncome(annualIncome1);
      return Math.max(0, netIncome / 12 + monthlyRentalIncome - expenses);
    } else {
      const netIncome1 = CalculateNetIncome(annualIncome1);
      const netIncome2 = CalculateNetIncome(annualIncome2);
      return Math.max(0, (netIncome1 + netIncome2) / 12 + monthlyRentalIncome - expenses);
    }
  };

  // Present value at (product rate + buffer)
  const maxLoanAt = (productRatePct, bufferPct) => {
    if (numberOfApplicants === 0 || annualIncome1 < 12000) return 0;
    const mpay = monthlyPay();
    if (mpay < 1) return 0;
    return CalculatePV(productRatePct + bufferPct, year * 12, mpay);
  };

  // Headline should match Lender 1 → entered rate + global buffer
  const baseMaxLoan = maxLoanAt(enteredRate, serviceBuffer);

  // Lender rules
  const L2_DISPLAY_RATE = 5.79;
  const L2_CALC_RATE = 4.79;  // with 2% buffer below
  const L3_RATE = 5.14;

  const scenarios = useMemo(() => {
    const lenders = [
      {
        key: "S1",
        label: "Lender 1",
        tag: "Best rate",
        displayRate: enteredRate,
        calcRate: enteredRate,
        buffer: serviceBuffer,           // 3%
        pills: ["Fixed Rate"],
      },
      {
        key: "S2",
        label: "Lender 2",
        tag: "Highest limit",
        displayRate: L2_DISPLAY_RATE,    // shown to user
        calcRate: L2_CALC_RATE,          // calc rate
        buffer: 2,                       // 2% buffer → higher capacity
        pills: ["Redraw Facility", "Offset Account"],
      },
      {
        key: "S3",
        label: "Lender 3",
        tag: "All Rounder",
        displayRate: L3_RATE,
        calcRate: L3_RATE,
        buffer: serviceBuffer,           // 3%
        pills: ["Redraw Facility", "Offset Account"],
      },
    ];

    return lenders.map((d) => ({
      ...d,
      loan: maxLoanAt(d.calcRate, d.buffer),
    }));
  }, [
    enteredRate,
    serviceBuffer,
    numberOfApplicants,
    numberOfDependant,
    annualIncome1,
    annualIncome2,
    monthlyRentalIncome,
    creditCardLimit,
    personalLoanRepay,
    homeLoanRepay,
    year,
  ]);

  const calcMonthlyRepayment = (loan, rPct, y) => {
    const n = y * 12;
    const i = (rPct / 100) / 12;
    if (loan <= 0 || n <= 0 || i <= 0) return 0;
    return (loan * i) / (1 - Math.pow(1 + i, -n));
  };

  const Money0 = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

  // ===== SNAPSHOT GATING (freeze until "Show Result" event) =====
  const [snap, setSnap] = useState(null);
  const buildSnapshot = () => ({
    baseMaxLoan,
    scenarios,
    rate: enteredRate,
    year,
  });

  useEffect(() => {
    const handler = () => setSnap(buildSnapshot());
    if (typeof window !== "undefined") {
      window.addEventListener("bc-show-result", handler);
      return () => window.removeEventListener("bc-show-result", handler);
    }
  }, [baseMaxLoan, scenarios, enteredRate, year]);

  useEffect(() => {
    if (showToken != null) setSnap(buildSnapshot());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToken]);

  const resultsAreHidden = snap == null;

  const displayBaseMaxLoan = resultsAreHidden ? 0 : snap.baseMaxLoan;
  const displayScenarios = resultsAreHidden ? [] : snap.scenarios;
  const displayRate = resultsAreHidden ? enteredRate : snap.rate;
  const displayYear = resultsAreHidden ? year : snap.year;

  // Popup with selection effect (centered "Selected" badge) + new CTA
  const openLenderWindow = () => {
    if (resultsAreHidden) return;

    const cards = displayScenarios.map((s) => ({
      ...s,
      repay: calcMonthlyRepayment(s.loan, s.displayRate, displayYear), // repayments shown at displayed rate
    }));

    const win = window.open("", "_blank");
    if (!win) return;

    const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Suggested Lenders</title>
<style>
  :root { --teal:#25C5FA; --border:#19aacd; --text:#111; --black:#000; --white:#fff; --sel:#111; --selbg:#f5f7f8; }
  * { box-sizing: border-box; }
  body { font-family: Calibri, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: var(--text); margin: 0; padding: 24px; background: #fff; }
  .page { max-width: 960px; margin: 0 auto; }
  a { color: inherit; }
  .topbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; }
  .back { text-decoration:none; font-weight:700; }
  .legal { text-decoration:underline; font-size:14px; opacity:.8; }
  h1 { margin: 8px 0; font-size: 36px; font-weight: 800; }
  .sub, .note { margin: 0 0 16px; font-size: 16px; }
  .card { border:2px solid var(--border); border-radius:12px; padding:16px; margin-bottom:16px; cursor:pointer; transition: box-shadow .15s ease, border-color .15s ease, background .15s ease; position:relative; }
  .card:hover { box-shadow: 0 6px 18px rgba(0,0,0,0.07); }
  .card.selected { border-color: var(--sel); background: var(--selbg); }
  .selectedBadge {
    position:absolute; top:50%; left:50%; transform:translate(-50%, -50%);
    font-size:12px; font-weight:800; border:2px solid var(--sel);
    border-radius:999px; padding:6px 12px; background:#fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  .cardHead { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; }
  .lTitle { font-weight:800; font-size:18px; }
  .tag { font-size:12px; text-transform:uppercase; letter-spacing:.03em; }
  .pills { display:flex; gap:8px; margin:8px 0 16px; flex-wrap: wrap; }
  .pill { background:#e6f9fe; color:#037; padding:6px 10px; border-radius:999px; font-weight:700; font-size:13px; border:1px solid var(--border); }
  .row { display:flex; justify-content:space-between; align-items:flex-end; gap:12px; }
  .amount { font-weight:800; font-size:28px; }
  .caption { font-size:12px; text-transform:uppercase; opacity:.8; margin-top:4px; }
  .grid { display:grid; grid-template-columns:repeat(2,minmax(160px,1fr)); gap:8px 24px; margin-top:12px; }
  .k { font-size:13px; opacity:.85; }
  .v { font-weight:800; }
  .ctaBar { margin-top:20px; }
  .btnFull {
    display:block; width:100%; height:58px; border-radius:12px;
    border:2px solid var(--black); background: var(--white); color: var(--black);
    font-size:16px; font-weight:700; text-align:center; text-decoration:none; line-height:58px;
    transition: transform .15s ease;
  }
  .btnFull:hover { transform: translateY(1px); }
  @media (max-width:600px){
    body { padding:16px; }
    .row { flex-direction:column; align-items:flex-start; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="topbar">
      <a class="back" href="https://www.mortgagematrix.com.au/home-loan-borrowing-power-calculator">← Go Back</a>
      <a class="legal" href="https://www.mortgagematrix.com.au/assumptions" target="_blank" rel="noopener">Assumptions & Disclaimer*</a>
    </div>

    <h1>Suggested Lenders</h1>
    <p class="sub">These are just early estimates. Tap a card to select a lender.</p>
    <p class="note">For an accurate comparison, and to see the lender names we recommend speaking to your broker.</p>

    ${cards.map(c => `
      <div class="card"
           id="card-${c.key}"
           data-key="${c.key}"
           data-loan="${c.loan}"
           tabindex="0"
           role="button"
           onclick="selectLender('${c.key}')"
           onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();selectLender('${c.key}');}">
        <div class="cardHead">
          <div>
            <div class="lTitle">${c.label}</div>
            <div class="tag">${c.tag}</div>
          </div>
          <div style="text-align:right">
            <div class="amount">${(c.repay).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}</div>
            <div class="caption">Monthly repayments</div>
          </div>
        </div>

        <div class="pills">
          ${c.pills.map(p => `<span class="pill">${p}</span>`).join("")}
        </div>

        <div class="row">
          <div>
            <div class="amount">${Math.round(c.loan).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })}</div>
            <div class="caption">You Can Borrow</div>
          </div>
          <div class="grid">
            <div><span class="k">Interest rate</span><div class="v">${Number(c.displayRate).toFixed(2)}%</div></div>
            <div><span class="k">Comparison rate</span><div class="v">${(Number(c.displayRate) + 0.22).toFixed(2)}%</div></div>
          </div>
        </div>
      </div>
    `).join("")}

    <div class="ctaBar">
      <a class="btnFull" href="https://www.mortgagematrix.com.au/get-started" target="_blank" rel="noopener">
        Speak to a Broker
      </a>
    </div>
  </div>

  <script>
    (function(){
      const KEY = 'mm_selected_lender';
      function mark(el){
        document.querySelectorAll('.card').forEach(n => {
          n.classList.remove('selected');
          const b = n.querySelector('.selectedBadge');
          if (b) b.remove();
        });
        el.classList.add('selected');
        const badge = document.createElement('div');
        badge.className = 'selectedBadge';
        badge.textContent = 'Selected';
        el.appendChild(badge);
      }
      function selectLender(k){
        const el = document.getElementById('card-'+k);
        if(!el) return;
        localStorage.setItem(KEY, k);
        mark(el);
        try {
          window.opener && window.opener.postMessage({ type: 'mm-lender-selected', lenderKey: k }, '*');
        } catch(e){}
      }
      window.selectLender = selectLender;

      // Restore previous selection or default to highest borrowing limit
      const saved = localStorage.getItem(KEY);
      if (saved && document.getElementById('card-'+saved)) {
        mark(document.getElementById('card-'+saved));
      } else {
        const cards = Array.from(document.querySelectorAll('.card'));
        if (cards.length) {
          const maxEl = cards.reduce((m, el) => (+el.dataset.loan > +m.dataset.loan ? el : m), cards[0]);
          mark(maxEl);
          localStorage.setItem(KEY, maxEl.id.replace('card-',''));
        }
      }
    })();
  </script>
</body>
</html>`;
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <div className={styles.result_view}>
      <div className="col-m-6">
        <label className={`form-label ${styles.label_main}`}>You may be able to borrow up to</label>
        <div className={styles.loan}>
          {resultsAreHidden ? "$000,000" : Money0.format(displayBaseMaxLoan)}
        </div>
      </div>

      <hr className={styles.hr} />

      <div className="col-m-6">
        {resultsAreHidden ? (
          <div style={{ padding: "8px 10px", opacity: 0.7 }}>
            Click <strong>Next</strong> to add your rental income and liabilities.
          </div>
        ) : (
          <BorrowingCapacityResultPay loan={displayBaseMaxLoan} rate={displayRate} year={displayYear} />
        )}
      </div>

      <hr className={styles.hr} />

      {/* Buttons row (centered) */}
      <div className={styles.buttonRow}>
        <button
          type="button"
          className={styles.infoButton}
          onClick={openLenderWindow}
          disabled={resultsAreHidden}
          aria-disabled={resultsAreHidden}
          title={resultsAreHidden ? "Click Show Result first" : "View lender options"}
        >
          Suggested Lenders <span className={styles.arrow}>→</span>
        </button>

        <a
          href={resultsAreHidden ? undefined : "https://www.mortgagematrix.com.au/apply"}
          target={resultsAreHidden ? undefined : "_blank"}
          rel={resultsAreHidden ? undefined : "noopener noreferrer"}
          className={styles.linkWrapper}
          aria-disabled={resultsAreHidden}
          onClick={(e) => { if (resultsAreHidden) e.preventDefault(); }}
        >
          <button className={`${styles.infoButton} ${styles.applyNowButton}`} disabled={resultsAreHidden}>
            Apply Now <span className={styles.arrow}>→</span>
          </button>
        </a>
      </div>

      <div className={styles.disclaimer}>
        This is an estimate of your borrowing capacity; if you choose a home
        loan with one of our panel of lenders, your borrowing capacity may vary
        based on your loan options. Learn more about how our calculator works
        below.
      </div>
    </div>
  );
};

export default BorrowingCapacityResult;
